import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import OperationReviewModal from './OperationReviewModal';
import SolveProgressV4Modal from './SolveProgressV4Modal';
import SolveResultV4Page from './SolveResultV4Page';
import SolverConfigurationModal, { DEFAULT_SOLVER_CONFIG, SolverConfig } from './SolverConfigurationModal';
import RunHistoryTab from './RunHistoryTab';
import IntervalSolveTab from './IntervalSolveTab';
import StandingDutyTab from './StandingDutyTab';
import RosterExceptionRepair from '../roster/RosterExceptionRepair';
import api from '../../services/api';
import {
    DepartmentFilterValue,
    filterBatchesByDepartment,
    getDefaultSelectedBatchIds,
    getVisibleSelectedBatchIds,
} from './batchSelection';
import {
    WxbBulkActionBar,
    WxbButton,
    WxbCard,
    WxbDataTable,
    WxbDatePicker,
    WxbFilterBar,
    WxbIcon,
    WxbPageHeader,
    WxbPageShell,
    WxbSelect,
    WxbTabs,
    WxbTag,
} from '../wxb-ui';
import type { WxbTagColor } from '../wxb-ui';

interface BatchPlan {
    id: number;
    batch_code: string;
    template_name: string; // Product in mockup
    team_id?: number;
    team_name?: string;
    team_code?: string;
    plan_status: string;
    planned_start_date: string;
    planned_end_date: string;
    // 基准日期 Day0（后端按模板 min_day 从最早工序日 planned_start_date 反推）
    day0_date?: string | null;
}

interface Team {
    id: number;
    teamName: string;
    teamCode: string;
}

const MonthlyBatchSelector: React.FC = () => {
    const [selectedMonth, setSelectedMonth] = useState<Dayjs>(dayjs()); // 默认当前月份
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<BatchPlan[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [modalVisible, setModalVisible] = useState(false);

    // Solver Configuration State
    const [configVisible, setConfigVisible] = useState(false);
    const [solverConfig, setSolverConfig] = useState<SolverConfig>(DEFAULT_SOLVER_CONFIG);

    // Department Filter State
    const [teams, setTeams] = useState<Team[]>([]);
    const [selectedDepartment, setSelectedDepartment] = useState<DepartmentFilterValue>('all');
    const [loadingTeams, setLoadingTeams] = useState(false);

    // Progress Modal State
    const [progressVis, setProgressVis] = useState(false);
    const [resultVis, setResultVis] = useState(false);
    const [currentRunId, setCurrentRunId] = useState<number | null>(null);
    const [currentRunStatus, setCurrentRunStatus] = useState<string | null>(null);

    const fetchTeams = async () => {
        setLoadingTeams(true);
        try {
            const { data: result } = await api.get<Team[]>('/organization/solver-teams');
            if (Array.isArray(result)) {
                setTeams(result);
            }
        } catch (error) {
            console.error('Failed to fetch teams:', error);
        } finally {
            setLoadingTeams(false);
        }
    };

    useEffect(() => {
        fetchTeams();
    }, []);

    const fetchData = async (month: Dayjs) => {
        setLoading(true);
        try {
            const startDate = month.startOf('month').format('YYYY-MM-DD');
            const endDate = month.endOf('month').format('YYYY-MM-DD');

            const { data: result } = await api.get('/batch-plans', {
                params: { start_date: startDate, end_date: endDate },
            });

            if (result.success && Array.isArray(result.data)) {
                setData(result.data);
            } else {
                // Fallback if API structure is different or returns error
                if (Array.isArray(result)) {
                    setData(result);
                } else {
                    console.error("Unexpected API response:", result);
                    message.error('加载批次数据失败');
                }
            }
        } catch (error) {
            console.error('Error fetching batches:', error);
            message.error('获取批次数据失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(selectedMonth);
    }, [selectedMonth]);

    useEffect(() => {
        setSelectedRowKeys(getDefaultSelectedBatchIds(data, selectedDepartment));
    }, [data, selectedDepartment]);

    const handleMonthChange = (date: Dayjs | null) => {
        if (date) {
            setSelectedMonth(date);
        }
    };

    const handleDepartmentChange = (value: DepartmentFilterValue) => {
        setSelectedDepartment(value);
        // 自动同步到高级配置
        if (value === 'all') {
            setSolverConfig((prev) => ({ ...prev, team_ids: [] }));
        } else {
            setSolverConfig((prev) => ({
                ...prev,
                team_ids: [value],
            }));
        }
        // 自动勾选过滤后的 ACTIVATED 批次
        setSelectedRowKeys(getDefaultSelectedBatchIds(data, value));
    };

    // 高级配置关闭时反向同步部门筛选器
    const handleConfigClose = () => {
        setConfigVisible(false);
        const ids = solverConfig.team_ids || [];
        const nextDepartment = ids.length === 1 ? ids[0] : 'all';
        setSelectedDepartment(nextDepartment);
        setSelectedRowKeys(getDefaultSelectedBatchIds(data, nextDepartment));
    };

    // 计算过滤后的数据
    const filteredData = Array.isArray(data)
        ? filterBatchesByDepartment(data, selectedDepartment)
        : [];
    const visibleSelectedRowKeys = getVisibleSelectedBatchIds(selectedRowKeys, filteredData);
    const hasTeamScope = selectedDepartment !== 'all';
    const selectedTeam = hasTeamScope
        ? teams.find(team => team.id === selectedDepartment)
        : undefined;
    const selectedScopeLabel = selectedTeam?.teamName || '所选团队';
    const canRunRosterOnly = hasTeamScope && visibleSelectedRowKeys.length === 0;

    const handleScheduleSelected = () => {
        if (visibleSelectedRowKeys.length === 0) {
            if (canRunRosterOnly) {
                setModalVisible(true);
                return;
            }
            message.warning('请先选择一个团队，或至少选择一个批次进行排班。');
            return;
        }

        setModalVisible(true);
    };

    const handleResetSelection = () => {
        setSelectedRowKeys([]);
    };

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const handleSchedulingSuccess = (runId: number) => {
        setModalVisible(false); // Close review modal
        setCurrentRunId(runId);
        setCurrentRunStatus('RUNNING');
        setProgressVis(true); // Open progress modal
    };

    const handleProgressClose = () => {
        setProgressVis(false);
    };

    const rowSelection = {
        selectedRowKeys: visibleSelectedRowKeys,
        onChange: onSelectChange,
    };

    const getPlanStatusColor = (status?: string): WxbTagColor => {
        if (status === 'IN PROGRESS' || status === 'ACTIVATED') return 'blue';
        if (status === 'COMPLETED') return 'green';
        if (status === 'PENDING') return 'amber';
        return 'neutral';
    };

    const getRunStatusColor = (status?: string | null): WxbTagColor => {
        if (status === 'COMPLETED' || status === 'APPLIED') return 'green';
        if (status === 'FAILED') return 'red';
        if (status === 'STOPPING') return 'amber';
        if (status === 'RUNNING' || status === 'QUEUED') return 'blue';
        return 'neutral';
    };

    const rememberedRunStatus = currentRunStatus ?? 'RUNNING';
    const rememberedRunTerminal = ['COMPLETED', 'APPLIED', 'FAILED'].includes(rememberedRunStatus);
    const showLiveRunBar = currentRunId !== null && !progressVis && !resultVis;

    const columns: ColumnsType<BatchPlan> = [
        {
            title: '批次编号',
            dataIndex: 'batch_code',
            key: 'batch_code',
            sorter: (a, b) => a.batch_code.localeCompare(b.batch_code),
        },
        {
            title: '产品',
            dataIndex: 'template_name',
            key: 'template_name',
            render: (text) => text || '-',
        },
        {
            title: '状态',
            dataIndex: 'plan_status',
            key: 'plan_status',
            render: (status) => {
                return <WxbTag color={getPlanStatusColor(status)}>{status || 'DRAFT'}</WxbTag>;
            },
        },
        {
            title: '部门',
            dataIndex: 'team_name',
            key: 'team_name',
            render: (text) => text || '-',
        },
        {
            title: 'Day0',
            dataIndex: 'day0_date',
            key: 'day0_date',
            defaultSortOrder: 'ascend',
            sorter: (a, b) => {
                const av = a.day0_date ? dayjs(a.day0_date).valueOf() : Number.POSITIVE_INFINITY;
                const bv = b.day0_date ? dayjs(b.day0_date).valueOf() : Number.POSITIVE_INFINITY;
                return av - bv;
            },
            render: (text) => text || '-',
        },
        {
            title: '开始日期',
            dataIndex: 'planned_start_date',
            key: 'planned_start_date',
            sorter: (a, b) => dayjs(a.planned_start_date).unix() - dayjs(b.planned_start_date).unix(),
        },
        {
            title: '结束日期',
            dataIndex: 'planned_end_date',
            key: 'planned_end_date',
            sorter: (a, b) => dayjs(a.planned_end_date).unix() - dayjs(b.planned_end_date).unix(),
        },
    ];

    return (
        <WxbPageShell size="full" gap="md" className="solver-v4-shell">
            <WxbPageHeader
                eyebrow="Solver V4"
                title="排班调度"
                description="选择月度批次、区间求解或值班任务后启动 V4 自动排班。"
                actions={(
                    <WxbButton
                        type="button"
                        variant="secondary"
                        onClick={() => setConfigVisible(true)}
                    >
                        <WxbIcon name="cip-system" size={16} />
                        高级配置
                    </WxbButton>
                )}
            />

            <WxbCard className="solver-v4-card" noPadding>
            <WxbTabs
                defaultActiveKey="batches"
                items={[
                    {
                        key: 'batches',
                        label: (
                            <span className="solver-v4-tab-label">
                                <WxbIcon name="batch-record" size={15} />
                                批次列表
                            </span>
                        ),
                        children: (
                            <div className="solver-v4-tab-panel">
                                <WxbFilterBar
                                    resultCount={filteredData.length}
                                    resultLabel="个批次"
                                    filters={(
                                        <>
                                            <WxbSelect
                                                value={selectedDepartment}
                                                onChange={handleDepartmentChange}
                                                loading={loadingTeams}
                                                className="solver-v4-filter-select"
                                                options={[
                                                    { value: 'all', label: '所有部门' },
                                                    ...teams.map(team => ({
                                                        value: team.id,
                                                        label: team.teamName,
                                                    })),
                                                ]}
                                            />
                                            <WxbDatePicker
                                                picker="month"
                                                value={selectedMonth}
                                                onChange={handleMonthChange}
                                                allowClear={false}
                                                className="solver-v4-month-picker"
                                            />
                                        </>
                                    )}
                                />

                                <WxbDataTable<BatchPlan>
                                    rowSelection={rowSelection}
                                    columns={columns}
                                    dataSource={filteredData}
                                    rowKey="id"
	                                    loading={loading}
	                                    density="standard"
	                                    scroll={{ x: 980 }}
	                                    emptyState={{ description: '当前月份没有可排班批次' }}
                                    pagination={{
                                        total: filteredData.length,
                                        pageSize: 10,
                                        showSizeChanger: true,
                                        showTotal: (total) => `共 ${total} 条`
                                    }}
                                />

                                <WxbBulkActionBar
                                    selectedCount={visibleSelectedRowKeys.length}
                                    onClear={handleResetSelection}
                                    clearLabel="重置选择"
                                    summary={(
                                        <span className="solver-v4-selection-text">
                                            已选 <strong>{visibleSelectedRowKeys.length}</strong> / 共 {filteredData.length} 个批次
                                        </span>
                                    )}
                                    actions={[
                                        {
                                            key: 'schedule',
                                            label: '排班选中批次',
                                            variant: 'primary',
                                            onClick: handleScheduleSelected,
                                        },
                                    ]}
                                />
                                {visibleSelectedRowKeys.length === 0 && (
                                    <div className="solver-v4-action-footer">
                                        <span className="solver-v4-selection-text">
                                            已选 <strong>0</strong> / 共 {filteredData.length} 个批次
                                            {canRunRosterOnly && (
                                                <>
                                                    {' '}
                                                    <WxbTag color="blue">仅人员排班</WxbTag>
                                                </>
                                            )}
                                        </span>
                                        <WxbButton type="button" variant="primary" onClick={handleScheduleSelected}>
                                            {canRunRosterOnly ? '排班本团队人员' : '排班选中批次'}
                                        </WxbButton>
                                    </div>
                                )}
                            </div>
                        ),
                    },
                    {
                        key: 'interval',
                        label: (
                            <span className="solver-v4-tab-label">
                                <WxbIcon name="hold-time" size={15} />
                                区间求解
                            </span>
                        ),
                        children: <IntervalSolveTab />,
                    },
                    {
                        key: 'duties',
                        label: (
                            <span className="solver-v4-tab-label">
                                <WxbIcon name="kanban" size={15} />
                                值班任务
                            </span>
                        ),
                        children: <StandingDutyTab />,
                    },
                    {
                        key: 'history',
                        label: (
                            <span className="solver-v4-tab-label">
                                <WxbIcon name="oos-clock" size={15} />
                                历史记录
                            </span>
                        ),
                        children: <RunHistoryTab />,
                    },
                    {
                        key: 'exception-repair',
                        label: (
                            <span className="solver-v4-tab-label">
                                <WxbIcon name="deviation" size={15} />
                                异常修复
                            </span>
                        ),
                        children: <RosterExceptionRepair embedded />,
                    },
                ]}
            />
	            </WxbCard>

	            {showLiveRunBar && (
	                <div className="solver-v4-live-run-bar" role="status">
	                    <div className="solver-v4-live-run-copy">
	                        <WxbIcon name="oos-clock" size={16} />
	                        <span>当前求解 Run #{currentRunId}</span>
	                        <WxbTag color={getRunStatusColor(rememberedRunStatus)}>{rememberedRunStatus}</WxbTag>
	                    </div>
	                    <div className="solver-v4-action-group">
	                        {rememberedRunTerminal ? (
	                            <WxbButton type="button" variant="primary" onClick={() => setResultVis(true)}>
	                                查看结果
	                            </WxbButton>
	                        ) : (
	                            <WxbButton type="button" variant="primary" onClick={() => setProgressVis(true)}>
	                                继续查看进度
	                            </WxbButton>
	                        )}
	                    </div>
	                </div>
	            )}

	            <OperationReviewModal
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                batchIds={visibleSelectedRowKeys as number[]}
                month={selectedMonth}
                onSuccess={handleSchedulingSuccess}
                solverConfig={solverConfig}
                rosterOnly={canRunRosterOnly}
                scopeLabel={selectedScopeLabel}
            />

            <SolverConfigurationModal
                visible={configVisible}
                config={solverConfig}
                onConfigChange={setSolverConfig}
                onClose={handleConfigClose}
            />

            <SolveProgressV4Modal
                visible={progressVis}
	                runId={currentRunId}
	                onCancel={handleProgressClose}
	                onStatusChange={setCurrentRunStatus}
	                onViewResults={(rid) => {
	                    setProgressVis(false);
	                    setResultVis(true);
                }}
            />

            <SolveResultV4Page
                visible={resultVis}
                runId={currentRunId}
                onClose={() => setResultVis(false)}
            />
        </WxbPageShell>
    );
};

export default MonthlyBatchSelector;
